/**
 * EVO — hisobot davri.
 *
 * ============================================================================
 * SANA `Date` GA AYLANTIRILMAYDI — MAHALLIY KOMPONENTLAR BILAN HISOBLANADI
 * ============================================================================
 * `format.ts` dagi sabab bu yerda ham amal qiladi: `new Date('2026-08-08')`
 * UTC yarim tunini beradi, Toshkent esa UTC+5. Kunlik hisobotda bu bir kunlik
 * siljish, ya'ni «bugungi savdo» ekranida kechagi raqam.
 *
 * Shuning uchun barcha hisob-kitob `getFullYear()/getMonth()/getDate()`
 * (mahalliy) ustida bajariladi va natija `YYYY-MM-DD` satri bo'lib chiqadi —
 * API aynan shuni kutadi (`reports.controller.ts` DATE regexi).
 *
 * ============================================================================
 * HAFTA DUSHANBADAN BOSHLANADI
 * ============================================================================
 * `Date.getDay()` yakshanbani 0 deb qaytaradi (AQSh an'anasi). O'zbekistonda
 * ish haftasi dushanbadan boshlanadi va «shu hafta savdosi» yakshanbadan
 * hisoblansa, dushanba kuni ertalab hisobot bo'sh chiqadi.
 */

export interface Period {
  readonly from: string;
  readonly to: string;
}

/**
 * Uzbekcha preset'lar. Tartib ekranda ham shu — eng tor davrdan eng kengiga.
 * `custom` ro'yxatda yo'q: u tugma emas, «sanani qo'lda kiritdim» holati.
 */
export const PERIOD_PRESETS = ['today', 'thisWeek', 'thisMonth', 'lastMonth', 'thisYear'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** `Date` → `YYYY-MM-DD`, MAHALLIY vaqt zonasida (`toISOString` emas). */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** `YYYY-MM-DD` ekanini tekshiradi — URL dan kelgan qiymat ishonchsiz. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE.test(value);
}

/**
 * Preset bo'yicha davr.
 *
 * `today` parametri test uchun: «bugun» ni qattiq bog'lamasdan tekshirish
 * imkonini beradi. Ishlab turgan ilovada u har doim `new Date()`.
 */
export function presetPeriod(preset: PeriodPreset, today: Date = new Date()): Period {
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();

  switch (preset) {
    case 'today': {
      const iso = toIsoDate(today);
      return { from: iso, to: iso };
    }

    case 'thisWeek': {
      // `getDay()`: 0 = yakshanba. Dushanbagacha qancha orqaga qaytish kerak:
      // dushanba (1) → 0 kun, yakshanba (0) → 6 kun.
      const weekday = today.getDay();
      const backToMonday = weekday === 0 ? 6 : weekday - 1;
      const monday = new Date(year, month, day - backToMonday);
      const sunday = new Date(year, month, day - backToMonday + 6);
      return { from: toIsoDate(monday), to: toIsoDate(sunday) };
    }

    case 'thisMonth': {
      // `new Date(year, month + 1, 0)` — keyingi oyning «0-kuni», ya'ni joriy
      // oyning oxirgi kuni. Oy uzunligini qo'lda hisoblash (28/29/30/31) shart emas.
      return {
        from: toIsoDate(new Date(year, month, 1)),
        to: toIsoDate(new Date(year, month + 1, 0)),
      };
    }

    case 'lastMonth': {
      // `month - 1` yanvarda `-1` bo'ladi va `Date` uni o'tgan yil dekabriga
      // o'zi ko'chiradi — qo'lda yil kamaytirish kerak emas.
      return {
        from: toIsoDate(new Date(year, month - 1, 1)),
        to: toIsoDate(new Date(year, month, 0)),
      };
    }

    case 'thisYear': {
      return {
        from: toIsoDate(new Date(year, 0, 1)),
        to: toIsoDate(new Date(year, 11, 31)),
      };
    }
  }
}

/** Standart davr — SHU OY. Rahbar ekranni ochganda birinchi savoli shu. */
export function defaultPeriod(): Period {
  return presetPeriod('thisMonth');
}

/** Davr ayni shu preset bilan bir xilmi — tugmani «faol» qilib ko'rsatish uchun. */
export function matchesPreset(period: Period, preset: PeriodPreset, today: Date = new Date()): boolean {
  const candidate = presetPeriod(preset, today);
  return candidate.from === period.from && candidate.to === period.to;
}

/**
 * Davr teskari kiritilgan bo'lsa (`from > to`) — almashtiriladi.
 *
 * Foydalanuvchi ko'pincha avval `to` ni o'zgartiradi, keyin `from` ni; oraliqda
 * davr teskari bo'lib qoladi va server bo'sh natija qaytaradi. Bo'sh jadval
 * «ma'lumot yo'q» deb o'qiladi — bu esa yolg'on.
 */
export function normalizePeriod(period: Period): Period {
  return period.from <= period.to ? period : { from: period.to, to: period.from };
}
